import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SearchOutlined } from "@ant-design/icons";
import _ from "lodash";
import "./EmployeeListStyles.scss";
import {
  getEmployeeList,
  reset,
} from "../../../../slices/Admin/userManagement.slice";
import NewEmployee from "../NewEmployee/component";
import Loader from "../../../../components/Common/Loader";
import TableTitleBar from "../../../../components/TableTitleBar/TableTitleBar";
import { getLocalStorage } from "../../../../helpers/utils";
import ErrorAlert from "../../../../components/UI/ErrorAlert/ErrorAlert";
import EmployeeListTable from "./EmployeeListTable";
import ListHeader from "../../../../components/Common/ListHeader";
import useDebouncedSearch from "../../../../hooks/useDebouncedSearch";
import CrrInput from "../../../../components/UI/CrrInput/CrrInput";

const EmployeeList = ({ asPage = true, orgId = 0 }) => {
  const dispatch = useDispatch();
  const { employeeList, loading, employeeListError } = useSelector(
    (state) => state.userManagement
  );
  const [tableData, setTableData] = useState([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [paginationCurrentPage, setPaginationCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isListLoading, setIsListLoading] = useState(false);
  const [employeeToEdit, setEmployeeToEdit] = useState({});
  const { activeSurveyId } = useSelector((state) => state.employeeDashboard);
  const [searchItem, handleSearchChange] = useDebouncedSearch((value) => {
    populateEmployeeList(value, 1, pageSize);
    setPaginationCurrentPage(1);
  }, 900);

  // Ref to store the searchItem
  const isAlteredListRef = useRef();

  useEffect(() => {
    isAlteredListRef.current = searchItem || paginationCurrentPage > 1;
  }, [searchItem, paginationCurrentPage]);

  useEffect(() => {
    populateEmployeeList();
    return () => {
      dispatch(reset({ isListWithSearch: isAlteredListRef.current }));
    };
  }, []);

  const populateEmployeeList = (
    searchItem = "",
    currentPage = paginationCurrentPage,
    size = pageSize
  ) => {
    setIsListLoading(true);
    dispatch(
      getEmployeeList(
        encodeURIComponent(searchItem),
        currentPage,
        size,
        orgId,
        activeSurveyId || getLocalStorage("ACTIVE_SURVEY")
      )
    )
      .then(() => {
        setIsListLoading(false);
      })
      .catch(() => {
        setIsListLoading(false);
      });
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPaginationCurrentPage(1);
    populateEmployeeList(searchItem, 1, size);
  };

  const handleDrawerOpen = (employee) => {
    setEmployeeToEdit(employee);
    setDrawerVisible(true);
  };
  const isEmployeeListEmpty = tableData.length === 0 && !searchItem;
  return (
    <div className="employee-list list-wrapper">
      {asPage && (
        <div className="list-header">
          <div className="list-title">User Management</div>
          <ListHeader
            handleSearch={handleSearchChange}
            handleAddClick={() => handleDrawerOpen([])}
            searchPlaceholder="Search Employee"
            addButtonTitle="ADD EMPLOYEE"
            addButtonPlaceholder={
              <>
                {" "}
                ADD <span className="hidden-text">EMPLOYEE</span>
              </>
            }
          />
        </div>
      )}
      <div className="list-body" style={!asPage ? { height: "100%" } : {}}>
        {loading && asPage ? (
          <Loader />
        ) : (
          <>
            {!asPage && !isEmployeeListEmpty && (
              <TableTitleBar title="Employee List">
                <CrrInput
                  className="search search-input"
                  onKeyUp={handleSearchChange}
                  placeholder={"Search Employee"}
                />
              </TableTitleBar>
            )}
            {!isListLoading && (
              <ErrorAlert
                errorData={employeeListError}
                message={
                  tableData?.length === 0
                    ? searchItem
                      ? "No matching records found"
                      : "No employee is present under this organization."
                    : ""
                }
                style={{ marginBottom: asPage ? 0 : "30px" }}
              />
            )}
            {asPage && tableData?.length > 0 && (
              <div className="table-title">Employee List</div>
            )}
            <EmployeeListTable
              isListLoading={isListLoading}
              populateEmployeeList={populateEmployeeList}
              PAGE_SIZE={pageSize}
              onPageSizeChange={handlePageSizeChange}
              orgId={orgId}
              paginationCurrentPage={paginationCurrentPage}
              searchItem={searchItem}
              handleDrawerOpen={handleDrawerOpen}
              setPaginationCurrentPage={setPaginationCurrentPage}
              tableData={tableData}
              setTableData={setTableData}
            />
          </>
        )}
      </div>

      <NewEmployee
        drawerVisible={drawerVisible}
        setDrawerVisible={setDrawerVisible}
        onClose={() => setDrawerVisible(false)}
        populateEmployeeList={populateEmployeeList}
        isEdit={employeeToEdit && Object.keys(employeeToEdit).length}
        employeeData={employeeToEdit}
        searchItem={searchItem}
      />
    </div>
  );
};

export default EmployeeList;
